import { Source, Vector as VectorSource } from 'ol/source'
import { Layer, Vector as VectorLayer } from 'ol/layer'
import { Fill, Stroke, Style, Text } from 'ol/style'
import { Feature, MapBrowserEvent, Overlay } from 'ol'
import { Draw, Modify, Snap } from 'ol/interaction'
import { Map as MGMap } from '@maersk-global/map-layers'
import { CoordinatesUtils } from '../utils/coordinates-utils'
import { Geometry, LineString, Point } from 'ol/geom'
import { fromLonLat, toLonLat } from 'ol/proj'
import { Coordinate } from 'ol/coordinate'
import { getDistance as gcDistance } from 'ol/sphere'

const _PROJECTION = (<any>window)["appenv"].projection;

export interface RulerLayerOptions
{
    color?: string;
    displayTotals?: boolean;
}

export interface RulerUpdateDetails {
    distance: number,
    coords: number[][],
}

export interface RulerInteractions {
    ruler: boolean
}

export class RulerLayer extends EventTarget {
    constructor(options?: RulerLayerOptions) {
        super();
        this._color = options?.color || 'black';
        this._displayTotals = options?.displayTotals !== undefined ? options.displayTotals : true; 

        this._initialize();
    }

    assignMap(map: MGMap) {
        this._map = map;
        this._createTotalsDiv();
    }

    addInteractions(options: RulerInteractions) {
        this._ruler = new Draw({
            source: this._rulerVectorSource!,
            type: "LineString",
            stopClick: true
        });

        this._ruler.on('drawstart', (e) => {
            this._rulerVectorSource!.clear();
            this._totalsDist = 0;
            this._currentLine = e.feature as Feature<LineString>;
            const geometry = this._currentLine?.getGeometry();
            if (geometry instanceof LineString) {
                const coordinates = geometry.getCoordinates();
                this._startPoint = coordinates[0];
            }

            if (this._displayTotals) {
                this._updateTotals();
                this._totalsDiv!.style.visibility = 'visible';
            }

            this._map!.on('pointermove', this._cursorMoveHandler);

            // Remove handler when drawing ends
            this?._ruler?.once('drawend', () => {
                this._map!.un('pointermove', this._cursorMoveHandler);
                this._currentLine = null;
                this._calculateLengths(); // Finalize the line's total distance
            });
        });

        if (options.ruler) {
            this._map!.addInteraction(this._ruler!);
        }

        this._map!.addInteraction(this._modify!);
        
        this._map!.addInteraction(this._snap!);

        this._layer!.setVisible(true);
        document.body.style.cursor = 'crosshair';

        // if during the start we already have a defined feature, assume that we want to 
        // to default to modify that feature. This is the case if the startRoute has been set
        if (this._rulerVectorSource!.getFeatures().length === 0) {
            // start drawing from a potential start point
            this._extendStartPoint();
        }
    }

    removeInteractions() {
        this._layer!.setVisible(false);

        if (this._displayTotals) {
            this._totalsDiv!.style.visibility = 'hidden';
            this._updateTotals();
        }

        this._rulerVectorSource!.clear();
        this._totalsDist = 0;
        this._currentLine = null;

        this._map!.removeInteraction(this._modify!);
        this._map!.removeInteraction(this._snap!);
        this._map!.removeInteraction(this._ruler!);
        document.body.style.cursor = 'auto';


        if (this._map) {
            this._map!.un('pointermove', this._cursorMoveHandler);
        }
    }

    setColor(color: string) {
        this._color = color;
    }

    setStart(point: number[]) {
        this._startPoint = fromLonLat(point, _PROJECTION);

        if (this._layer!.getVisible()) {
            this._extendStartPoint();
        }

        this._map!.addInteraction(this._ruler!);
    }

    setStartRoute(points: number[][]) {
        let coords = points.map((p) => fromLonLat(p, _PROJECTION));
        this._startPoint = coords[0];

        // for plotting, we need to modify lines crossing the dateline so point 'train' is continuous.
        const continousLon = (lon1: number, lon2: number) => {
            if (Math.abs(lon2 - lon1) > 180) {
                return lon2 + Math.sign(lon1) * 360;
            }
            return lon2;
        }

        const primary: [number,number][] = [];
        let lastLon: number = points[0][0];
        for (let i = 0; i < points.length; i++)
        {
            let coord: [number, number] = [points[i][0], points[i][1]];
            lastLon = continousLon(lastLon, coord[0]);
            coord = [lastLon, coord[1]];
            primary.push(coord);
        }

        const shift360 = (coords: [number,number][], n: number) => {
            return coords.map(coord => [coord[0]+n*360, coord[1]] );
        }

        const shiftedFeatures: Feature<LineString>[] = [];
        const originalFeature = new Feature<LineString>(new LineString(points));
        originalFeature.getGeometry()?.transform('EPSG:4326', _PROJECTION);
        // add linestrings +/- N * 360 (to fix dateline problem while editing)
        [-2,-1,0,1,2].forEach(n => { 
            const coords = shift360(primary.map(p => [p[0], p[1]]), n);
            const shiftedFeature = new Feature<LineString>(new LineString(coords));
            shiftedFeature.getGeometry()?.transform('EPSG:4326', _PROJECTION);
            shiftedFeature.set("originalFeature", originalFeature);
            shiftedFeatures.push(shiftedFeature);
        });
           
        this._rulerVectorSource!.clear();
        this._ruler?.abortDrawing();
        this._rulerVectorSource!.addFeatures(shiftedFeatures);

        // the route is finalised, so remove draw/ruler interaction
        this._map!.removeInteraction(this._ruler!);
    }

    private _layer: Layer<Source>|null = null;
    private _color: string;
    private _startPoint: number[]|null = null;
    private _displayTotals: boolean = true;
    private _totalsDiv: HTMLDivElement|null = null;
    private _totalsOverlay: Overlay|null = null;
    private _ruler: Draw|null = null;
    private _modify: Modify|null = null;
    private _snap: Snap|null = null;
    private _map: MGMap|null = null;
    private _rulerVectorSource: VectorSource<Geometry>|null = null;
    private _currentLine: Feature<LineString>|null = null;
    private _currentLineCoords: number[][] = [];
    private _totalsDist: number = 0;
    private _totalsDistEl: HTMLSpanElement|null = null;
    private _totalsTimeEl: HTMLSpanElement|null = null;
    private _totalsSpeedEl: HTMLInputElement|null = null;

    get layer() {
        return this._layer;
    }

    private async _notify() {
        this.dispatchEvent(new CustomEvent('ruler', {
            detail: <RulerUpdateDetails>{
                distance: this._totalsDist,
                coords: this._currentLineCoords,
            }
        }));
    }

    private _extendStartPoint() {
        if (this._startPoint) {
            this._rulerVectorSource!.clear();
            let feature = new Feature<LineString>(new LineString([this._startPoint]));
            this._ruler!.extend(feature);
        }
    }

    private _cursorMoveHandler = (event: MapBrowserEvent<PointerEvent>) => {
        if (!this._currentLine || !this._map || !this._startPoint) {
            return;
        }

        const coords = this._currentLine.getGeometry()?.getCoordinates() ?? [];
        if (coords.length === 0) {
            return;
        }

        const cursorCoord = this._map.getCoordinateFromPixel(event.pixel);
        if (!cursorCoord) return;

        // cursor position as the latest point temporarily
        const lastCoord = coords[coords.length - 1];
        const tempCoords = [...coords.slice(0, -1), lastCoord, cursorCoord];

        let tempDist = 0;
        for (let i = 0; i < tempCoords.length - 1; i++) {
            const pos1 = toLonLat(tempCoords[i], _PROJECTION);
            const pos2 = toLonLat(tempCoords[i + 1], _PROJECTION);
            tempDist += gcDistance(pos1, pos2) / 1852; // Nautical miles
        }

        this._totalsDist = tempDist;
        this._totalsDistEl!.innerHTML = `Total (RL): ${this._totalsDist.toFixed(3)} Nm`;

        // Update the position of the overlay to the cursor's position
        this._totalsOverlay!.setPosition(cursorCoord);
    };

    private _calculateLengths() {
        if (!this._currentLine) return;

        const coords = this._currentLine.getGeometry()?.getCoordinates();
        
        
        let totalDist = 0;
        this._currentLineCoords = coords!.map((c) => { return toLonLat(c, _PROJECTION);});

        for (let i = 0; i < this._currentLineCoords.length - 1; i++) {
            const pos1 = this._currentLineCoords[i];
            const pos2 = this._currentLineCoords[i+1];
            const dist = gcDistance(pos1, pos2) / 1852;
            totalDist += dist;
        }

        this._totalsDist = totalDist;
        this._notify();

        if (this._displayTotals) {
            this._updateTotals();

            const coord = this._currentLine.getGeometry()!.getLastCoordinate();
            this._totalsOverlay!.setPosition(coord);
            this._totalsDiv!.style.visibility = 'visible';
        }
    }

    private _updateTotals() {
        const kts = +this._totalsSpeedEl!.value / 10;

        this._totalsDistEl!.innerHTML = `Total (RL): ${this._totalsDist.toFixed(3)} Nm`;
        if (Number.isFinite(this._totalsDist) && this._totalsDist > 0) {
            let minutes = +this._totalsDist * 60 / kts;

            const days = Math.floor(minutes / (24 * 60));
            minutes -= days * 24 * 60;
            const hours = Math.floor(minutes / 60);
            minutes = Math.round(Math.max(minutes - (hours * 60), 0)); // Potential rounding error prevention

            const timeStr = `${days.toString().padStart(2, '0')}d${hours.toString().padStart(2, '0')}h${minutes.toString().padStart(2, '0')}m`

            this._totalsTimeEl!.innerHTML = `Total time: ${timeStr} at ${kts.toFixed(1)} kt`;
        } else {
            this._totalsTimeEl!.innerHTML = `Total time: -`;
        }
    }

    private _styleFunction(feature: any) {
        const geometry = feature.getGeometry() as LineString;
        const styles = [
            new Style({
                stroke: new Stroke({
                    color: '#91D8F299',
                    lineDash: [10, 10],
                    width: 7,
                }),
            }),
            new Style({
                stroke: new Stroke({
                    color: this._color,
                    lineDash: [10, 10],
                    width: 3,
                }),
            })
        ];

        const wpLabel = (pos: Coordinate, num: Number) => {
            return new Style({
              geometry: new Point(pos),
              text: new Text({
                text: `${num}`,
                font: '16px MaerskText',
                fill: new Fill({
                    color: '#fff'
                }),
                stroke: new Stroke({color: 'rgba(0, 0, 0, 0.6)', width: 3}),
                rotateWithView: true,
              }),
              zIndex: 20
            })
        };

        let count = 0

        geometry.forEachSegment( (start: Coordinate, end: Coordinate) => {
            const dx = end[0] - start[0];
            const dy = end[1] - start[1];
            let rotation = Math.atan2(dy, dx);
            if (Math.abs(rotation) > Math.PI/2) rotation -= Math.PI;
            const midPoint = [(start[0]+end[0])*0.5, (start[1]+end[1])*0.5];

            const pos1 = toLonLat(start, _PROJECTION);
            const pos2 = toLonLat(end, _PROJECTION);

            const dist = CoordinatesUtils.calcDistance(pos1, pos2);

            // Segment distance labels
            styles.push(
              new Style({
                geometry: new Point(midPoint),
                text: new Text({
                    text: `${dist.rl.toFixed(0)} NM`,
                    font: '14px MaerskText',
                    textBaseline: 'bottom',
                    offsetY: -4,
                    fill: new Fill({
                        color: this._color,
                    }),
                    stroke: new Stroke({color: '#91D8F299', width: 3}),
                    rotateWithView: true,
                    rotation: -rotation,
                }),
              })
            );

            if (count === 0) styles.push( wpLabel(start, count++));
            styles.push( wpLabel(end, count++));
        });

        return styles;
    }

    private _createTotalsDiv() {
        if (!this._displayTotals) {
            return;
        }



        // NOTE: The Totals box is temporarily placed in an OpenLayers overlay
        //       and should be moved to a separate component when proper UI design is available.
        this._totalsDiv = document.createElement('div');
        this._totalsDiv.innerHTML = `
          <style>
           #totals {
               display: flex;
               flex-direction: column;
               background: #ffffff80;
               color: black;
               font: 14px MaerskText;
               padding: 8px;
           }

           #speed {
               width: 200px;
           }

           #time {
            font-variant-numeric: tabular-nums;
           }
          </style>
          <div id='totals'>
            <span id='dist'>Total length (RL): -</span>
            <span id='time'>Total hours: -</span>
            <input id='speed' type='range' min='60' max='240'>
          </div>
        `;

        this._totalsDistEl = this._totalsDiv.querySelector('#dist') as HTMLSpanElement;
        this._totalsTimeEl = this._totalsDiv.querySelector('#time') as HTMLSpanElement;
        this._totalsSpeedEl = this._totalsDiv.querySelector('#speed') as HTMLInputElement;

        this._totalsSpeedEl.addEventListener('input', this._updateTotals.bind(this) );

        this._totalsOverlay = new Overlay({
            element: this._totalsDiv,
            offset: [0, -15],
            positioning: 'bottom-center'
        })

        this._map!.addOverlay(this._totalsOverlay);
    }

    private _initialize() {
        this._rulerVectorSource = new VectorSource();

        this._rulerVectorSource.on('addfeature', e => {
            if (e.feature) {
                this._currentLine = <Feature<LineString>>e.feature;
                this._calculateLengths();
            }
        });

        this._layer = new VectorLayer({
            source: this._rulerVectorSource,
            style: this._styleFunction.bind(this),
            visible: false,
        });

        this._modify = new Modify({source: this._rulerVectorSource});
        this._modify.on('modifyend', e => {
            if (e.features) {
                this._currentLine = e.features.getArray()[0] as Feature<LineString>;
                this._rulerVectorSource!.clear();
                this._ruler?.abortDrawing();
                this._rulerVectorSource?.addFeature(this._currentLine);
                this._calculateLengths();
            }
        });
        this._snap = new Snap({source: this._rulerVectorSource});
    }
}
